import { useEffect, useRef } from "react";
import { updateSessionLocation } from "features/driver-sessions/services/driverSessionApi";
import type { LocationBody } from "types/driver.types";
import type { LatLng } from "shared/lib/tracking/geo";
import { bearingDegrees, haversineMeters } from "shared/lib/tracking/geo";
import { USE_MOCK_TRACKING_SSE_DRIVER } from "core/config/mockTracking";
import { getMockTrackingSseRoute } from "core/config/mockTrackingSseRoute";

const INTERVAL_IDLE_MS = 30_000;
const INTERVAL_ACTIVE_MS = 8_000;
/** Faster ticks when mocking so the customer map moves smoothly. */
const INTERVAL_MOCK_ACTIVE_MS = 5_000;
const INTERVAL_MOCK_IDLE_MS = 10_000;

interface UseDriverSessionLocationOptions {
  /** Shorter interval when order is picked_up or in_transit */
  activeDelivery?: boolean;
}

function buildPayload(
  pos: GeolocationPosition,
  prev: LatLng | null,
  prevTime: number,
): LocationBody {
  const lat = pos.coords.latitude;
  const lng = pos.coords.longitude;
  const body: LocationBody = { lat, lng };

  if (
    pos.coords.heading != null &&
    !Number.isNaN(pos.coords.heading) &&
    pos.coords.heading >= 0
  ) {
    body.heading = pos.coords.heading;
  }

  if (pos.coords.speed != null && pos.coords.speed >= 0) {
    body.speed = pos.coords.speed;
  } else if (prev && prevTime > 0) {
    const dt = (pos.timestamp - prevTime) / 1000;
    if (dt > 0.5) {
      const dist = haversineMeters(prev, { lat, lng });
      body.speed = dist / dt;
      if (body.heading == null && dist > 2) {
        body.heading = bearingDegrees(prev, { lat, lng });
      }
    }
  }

  return body;
}

function buildMockPayload(
  current: LatLng,
  prev: LatLng | null,
  elapsedMs: number,
): LocationBody {
  const body: LocationBody = { lat: current.lat, lng: current.lng };
  if (prev && elapsedMs > 0) {
    const dist = haversineMeters(prev, current);
    const speed = dist / (elapsedMs / 1000);
    if (speed > 0.1) {
      body.speed = Math.min(speed, 25);
      body.heading = bearingDegrees(prev, current);
    } else {
      body.speed = 6;
      body.heading = 90;
    }
  } else {
    body.speed = 8;
    body.heading = 90;
  }
  return body;
}

export function useDriverSessionLocation(
  sessionActive: boolean,
  options?: UseDriverSessionLocationOptions,
): void {
  const activeDelivery = options?.activeDelivery ?? false;

  const intervalMs = USE_MOCK_TRACKING_SSE_DRIVER
    ? activeDelivery
      ? INTERVAL_MOCK_ACTIVE_MS
      : INTERVAL_MOCK_IDLE_MS
    : activeDelivery
      ? INTERVAL_ACTIVE_MS
      : INTERVAL_IDLE_MS;

  const lastSent = useRef<number>(0);
  const prevFix = useRef<LatLng | null>(null);
  const prevTime = useRef<number>(0);
  const mockIndexRef = useRef(0);
  const mockRouteRef = useRef<LatLng[] | null>(null);

  useEffect(() => {
    if (!sessionActive) return;

    // Mock driver session location
    if (USE_MOCK_TRACKING_SSE_DRIVER) {
      if (!mockRouteRef.current) {
        mockRouteRef.current = getMockTrackingSseRoute();
        if (import.meta.env.DEV) {
          console.info(
            "[useDriverSessionLocation] MOCK_TRACKING_SSE enabled —",
            mockRouteRef.current.length,
            "waypoints, interval",
            intervalMs,
            "ms",
          );
        }
      }

      const tick = () => {
        const route = mockRouteRef.current!;
        const now = Date.now();
        if (now - lastSent.current < intervalMs - 500) return;
        lastSent.current = now;

        const current = route[mockIndexRef.current % route.length];
        mockIndexRef.current += 1;

        const elapsed = prevTime.current ? now - prevTime.current : intervalMs;
        const body = buildMockPayload(current, prevFix.current, elapsed);
        prevFix.current = { lat: body.lat, lng: body.lng };
        prevTime.current = now;
        updateSessionLocation(body).catch(() => undefined);
      };

      tick();
      const id = setInterval(tick, intervalMs);
      return () => clearInterval(id);
    }

    // Real driver session location
    if (!navigator.geolocation) return;

    const tick = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const now = Date.now();
          if (now - lastSent.current < intervalMs - 1000) return;
          lastSent.current = now;
          const body = buildPayload(pos, prevFix.current, prevTime.current);
          prevFix.current = { lat: body.lat, lng: body.lng };
          prevTime.current = pos.timestamp;
          updateSessionLocation(body).catch(() => undefined);
        },
        () => undefined,
        { enableHighAccuracy: true, maximumAge: 10000 },
      );
    };

    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [sessionActive, intervalMs]);
}
